import api from '../services/api';
import {
  base64ToString,
  renderHtmlToPdfBlob,
  triggerBlobDownload,
} from '../shared/utils/pdf-utils';

export type CrcContactStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING';
export type CrcContactOrigin = 'MANUAL' | 'IMPORTED' | 'CAMPAIGN' | 'FORM';
export type CrcPendencyStatus = 'ABERTA' | 'RESOLVIDA' | 'IGNORADA';
export type CrcPendencyPriority = 'BAIXA' | 'MEDIA' | 'ALTA';
export type CrcPendencyType =
  | 'NAO_CONFIRMADO'
  | 'FALTA'
  | 'SEM_RETORNO'
  | 'TRATAMENTO_INTERROMPIDO'
  | 'POS_ATENDIMENTO';

export interface ICrcContact {
  id: string;
  client_id?: string | null;
  client_name?: string | null;
  name: string;
  phone: string;
  email?: string | null;
  status: CrcContactStatus;
  origin: CrcContactOrigin;
  tags?: string[] | null;
  last_contact_at?: string | null;
  next_follow_up_at?: string | null;
  notes?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ICrcPendency {
  id: string;
  client_id: string;
  client_name?: string | null;
  phone?: string | null;
  email?: string | null;
  motivo: CrcPendencyType;
  acao_recomendada?: string | null;
  prioridade: CrcPendencyPriority;
  ultimo_contato?: string | null;
  dias_contato: number;
  situacao: CrcPendencyStatus;
  resolvido_por?: string | null;
  resolved_at?: string | null;
  schedule_id?: string | null;
  observacoes?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;

  assigned_user_id?: string | null;
  assigned_name?: string | null;
  professional_id?: string | null;
  professional_name?: string | null;
  specialty_id?: string | null;
  specialty_description?: string | null;
}

export interface IDashboardKpi {
  key: string;
  label: string;
  value: number | string;
  previous?: number | string;
  variation?: number;
  unit?: string;
  color?: string;
  icon?: string;
}

export interface IChartPoint {
  label: string;
  value: number;
  secondary_value?: number;
  date?: string;
  category?: string;
}

export interface IAudienceClient {
  id: string;
  code: number;
  corporate_name: string;
  cell_phone: string;
  email?: string | null;
  birthday: string;
  gender?: string | null;
  covenant?: string | null;
  last_visit_at?: string | null;
  total_spent?: number;
  treatment_status?: string | null;
}

export interface IRecurrentProcedureRow {
  procedure_id: string;
  procedure_code: number;
  procedure_description: string;
  procedure_price: number;
  is_recurrent: boolean;
  return_days: number | null;
  total_items: number;
  pending_returns: number;
  last_service_date: string | null;
  next_due_date: string | null;
  overdue_days: number | null;
  status: 'em_dia' | 'vence_hoje' | 'atrasado' | 'sem_atendimento';
}

export interface IRecurrentProceduresReport {
  rows: IRecurrentProcedureRow[];
  total_procedures_recurrent: number;
  total_items_with_return: number;
  total_pending_returns: number;
  total_overdue: number;
  total_due_today: number;
  generated_at: string;
}

export interface IFutureContactRow {
  client_id: string;
  client_name: string;
  client_cpf_cnpj: string;
  cell_phone: string | null;
  home_phone: string | null;
  office_phone: string | null;
  email: string | null;
  procedure_id: string;
  procedure_description: string;
  professional_id: string;
  professional_name: string;
  return_due_date: string;
  source: 'clinical' | 'dental';
}

export interface IFutureContactsReport {
  rows: IFutureContactRow[];
  generated_at: string;
}

export interface IAtendentePerformanceRow {
  atendente_id: string;
  atendente_nome: string;
  contatos_realizados: number;
  contatos_pendentes: number;
  pendencias_resolvidas: number;
  taxa_conversao: number;
  top_especialidades: { especialidade: string; total: number }[];
}

export interface IAtendentePerformanceReport {
  rows: IAtendentePerformanceRow[];
  generated_at: string;
}

export interface ICrcReportsOverview {
  overview: ICrcDashboardOverview;
  atendente_performance: IAtendentePerformanceReport;
  generated_at: string;
}

export interface ICrcDashboardOverview {
  kpis: IDashboardKpi[];
  contacts_by_status: IChartPoint[];
  pendencies_by_status: IChartPoint[];
  pendencies_by_priority: IChartPoint[];
  contacts_evolution: IChartPoint[];
  pendencies_evolution: IChartPoint[];
  top_contacts: ICrcContact[];
  overdue_pendencies: ICrcPendency[];
}

interface IResponseGet<T> {
  data: T[];
  count: number;
}

interface ICrcSearchContactParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: CrcContactStatus;
  origin?: CrcContactOrigin;
  owner_user_id?: string;
  atendente_user_id?: string;
  tag?: string;
  canal?: string;
  motivo?: string;
  situacao?: string;
  client_id?: string;
  start_date?: string;
  end_date?: string;
}

interface ICrcSearchPendencyParams {
  limit?: number;
  offset?: number;
  search?: string;
  situacao?: CrcPendencyStatus;
  prioridade?: CrcPendencyPriority;
  motivo?: CrcPendencyType;
  assigned_user_id?: string;
  professional_id?: string;
  specialty_id?: string;
  client_id?: string;
  dias_contato_min?: number;
  dias_contato_max?: number;
  due_date_from?: string;
  due_date_to?: string;
  overdue?: boolean;
}

interface ICrcDashboardOverviewParams {
  start_date?: string;
  end_date?: string;
  atendente_ids?: string[];
  professional_ids?: string[];
  especialidade?: string;
}

export const getCrcDashboardOverview =
  async (params: ICrcDashboardOverviewParams = {}): Promise<ICrcDashboardOverview> => {
    const { start_date, end_date, atendente_ids, professional_ids, especialidade } = params;
    const query = new URLSearchParams();
    if (start_date) query.set('start_date', start_date);
    if (end_date) query.set('end_date', end_date);
    if (atendente_ids && atendente_ids.length > 0) {
      atendente_ids.forEach((id) => query.append('atendente_ids[]', id));
    }
    if (professional_ids && professional_ids.length > 0) {
      professional_ids.forEach((id) => query.append('professional_ids[]', id));
    }
    if (especialidade) query.set('especialidade', especialidade);
    const qs = query.toString();
    const { data } = await api.get(
      `/crc/dashboard/overview${qs ? `?${qs}` : ''}`,
    );
    return data;
  };

export const getCrcContacts = async (
  params: ICrcSearchContactParams = {},
): Promise<IResponseGet<ICrcContact>> => {
  const {
    limit = 10,
    offset = 0,
    search,
    status,
    origin,
    owner_user_id,
    atendente_user_id,
    tag,
    canal,
    motivo,
    situacao,
    client_id,
    start_date,
    end_date,
  } = params;
  const query = new URLSearchParams();
  query.set('limit', String(limit));
  query.set('offset', String(offset));
  if (search) query.set('search', search);
  if (status) query.set('status', status);
  if (situacao) query.set('situacao', situacao);
  if (origin) query.set('origin', origin);
  if (canal) query.set('canal', canal);
  if (motivo) query.set('motivo', motivo);
  if (owner_user_id) query.set('owner_user_id', owner_user_id);
  if (atendente_user_id) query.set('atendente_user_id', atendente_user_id);
  if (client_id) query.set('client_id', client_id);
  if (start_date) query.set('start_date', start_date);
  if (end_date) query.set('end_date', end_date);
  if (tag) query.set('tag', tag);
  const { data } = await api.get(`/crc/contacts?${query.toString()}`);
  return data;
};

export const crcDeleteContact = async (id: string): Promise<void> => {
  await api.delete(`/crc/contacts/${id}`);
};

export const searchCrcContacts = async (
  search = '',
  limit = 20,
): Promise<{ data: ICrcContact[]; count: number }> => {
  const params = new URLSearchParams();
  params.set('search', search);
  params.set('limit', String(limit));
  const { data } = await api.get(`/crc/contacts/search?${params.toString()}`);
  return data;
};

export const postCrcContact = async (
  payload: Partial<ICrcContact> | Record<string, unknown>,
): Promise<ICrcContact> => {
  const { data } = await api.post('/crc/contacts', payload);
  return data;
};

export const patchCrcContact = async (
  id: string,
  payload: Partial<ICrcContact> | Record<string, unknown>,
): Promise<ICrcContact> => {
  const { data } = await api.patch(`/crc/contacts/${id}`, payload);
  return data;
};

export const getCrcPendencies = async (
  params: ICrcSearchPendencyParams = {},
): Promise<IResponseGet<ICrcPendency>> => {
  const {
    limit = 10,
    offset = 0,
    search,
    situacao,
    prioridade,
    motivo,
    assigned_user_id,
    professional_id,
    specialty_id,
    client_id,
    dias_contato_min,
    dias_contato_max,
  } = params;
  const query = new URLSearchParams();
  query.set('limit', String(limit));
  query.set('offset', String(offset));
  if (search) query.set('search', search);
  if (situacao) query.set('situacao', situacao);
  if (prioridade) query.set('prioridade', prioridade);
  if (motivo) query.set('motivo', motivo);
  if (assigned_user_id) query.set('assigned_user_id', assigned_user_id);
  if (professional_id) query.set('professional_id', professional_id);
  if (specialty_id) query.set('specialty_id', specialty_id);
  if (client_id) query.set('client_id', client_id);
  if (typeof dias_contato_min === 'number') query.set('dias_contato_min', String(dias_contato_min));
  if (typeof dias_contato_max === 'number') query.set('dias_contato_max', String(dias_contato_max));
  const { data } = await api.get(`/crc/pendencies?${query.toString()}`);
  return data;
};

export const getCrcPendencyTabs = async (): Promise<{
  all: number;
  unconfirmed: number;
  absent: number;
  no_return: number;
  interrupted: number;
  post_service: number;
}> => {
  const { data } = await api.get(`/crc/pendencies/tabs`);
  return data;
};

export const executePendencyQuickAction = async (
  id: string,
  payload: {
    acao: 'WHATSAPP' | 'LIGAR' | 'EMAIL' | 'AGENDAR_RETORNO';
    mensagem?: string;
  },
): Promise<any> => {
  const { data } = await api.post(`/crc/pendencies/${id}/actions`, payload);
  return data;
};

export const resolveCrcPendency = async (
  id: string,
  payload: {
    observacoes?: string;
    situacao?: CrcPendencyStatus;
  } = {},
): Promise<ICrcPendency> => {
  const { data } = await api.post(`/crc/pendencies/${id}/resolve`, payload);
  return data;
};

export const generateCrcPendencies = async (payload: {
  type?: CrcPendencyType;
  template_id?: string;
  filters?: Record<string, unknown>;
} = {}): Promise<{
  generated: number;
  already_exists?: number;
  created?: number;
  motivos?: Record<string, number>;
  pendencies: ICrcPendency[];
}> => {
  const { data } = await api.post('/crc/pendencies/generate', payload);
  return data;
};

export const crcSendWhatsapp = async (payload: {
  contact_id?: string;
  phone?: string;
  message: string;
  template_id?: string;
  placeholders?: Record<string, unknown>;
}): Promise<{ ok: boolean; queued: boolean; phone: string; wa_message_id?: string }> => {
  const { data } = await api.post('/crc/whatsapp/send', payload);
  return data;
};

export interface IExportCrcReportResponse {
  url?: string;
  filename: string;
  format?: string;
  mime_type?: string;
  content_base64?: string;
  generated_at: string;
}

const base64ToBlob = (base64: string, mime: string): Blob => {
  const byteChars = atob(base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime || 'application/octet-stream' });
};

const triggerDownload = (blob: Blob, filename: string): void => {
  const link = document.createElement('a');
  const blobUrl = URL.createObjectURL(blob);
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

const stripBom = (s: string): string => {
  if (!s) return s;
  if (s.charCodeAt(0) === 0xfeff) return s.slice(1);
  return s;
};

type SniffedFormat = 'csv' | 'html' | 'pdf' | 'unknown';

const sniffContentFormat = (rawText: string, mime?: string): SniffedFormat => {
  if (!rawText) return 'unknown';
  const cleaned = stripBom(rawText.trim());
  if (cleaned.length < 4) return 'unknown';
  const head = cleaned.slice(0, 16);
  if (/^%PDF-/.test(head)) return 'pdf';
  if (/^<(!DOCTYPE html|html|head|body|div|table|h[1-6]|p)\b/i.test(cleaned)) return 'html';
  const firstLine = cleaned.split(/\r?\n/)[0] || '';
  if (firstLine.length > 0) {
    const hasComma = firstLine.includes(',');
    const hasSemicolon = firstLine.includes(';');
    const hasTab = firstLine.includes('\t');
    if (hasComma || hasSemicolon || hasTab) {
      const quoteRatio = (firstLine.match(/"/g) || []).length / Math.max(1, firstLine.length);
      if (quoteRatio < 0.4) return 'csv';
    }
    if (/^(Relatório|Relatorio|CRC|===|Indicador|Situa[çc][aã]o|Atendente)/i.test(firstLine)) {
      return 'csv';
    }
  }
  const lower = cleaned.toLowerCase();
  if (lower.includes('<table') && lower.includes('</table>')) return 'html';
  if (lower.includes('<div') || lower.includes('<h1') || lower.includes('<html')) return 'html';
  if (mime) {
    if (/\/pdf/i.test(mime)) return 'pdf';
    if (/csv/i.test(mime)) return 'csv';
    if (/html/i.test(mime)) return 'html';
  }
  return 'unknown';
};

const ensureCorrectExtension = (
  filename: string,
  sniffed: SniffedFormat,
  requestedFormat?: 'pdf' | 'xlsx' | 'csv' | null,
): string => {
  const name = filename || `CRC_Relatorio_${Date.now()}`;
  const base = name.replace(/\.(xlsx|xls|csv|html?|pdf)$/i, '');
  if (sniffed === 'pdf') return `${base}.pdf`;
  if (sniffed === 'html') {
    if (requestedFormat === 'pdf') return `${base}.html`;
    return `${base}.html`;
  }
  if (sniffed === 'csv') return `${base}.csv`;
  if (requestedFormat === 'xlsx' || requestedFormat === 'csv') return `${base}.csv`;
  if (requestedFormat === 'pdf') return `${base}.pdf`;
  return name;
};

export const exportCrcReport = async (payload: {
  report_type: 'contacts' | 'pendencies' | 'overview' | 'performance' | 'atendente_performance' | 'full' | 'recurrent_procedures' | 'future_contacts';
  format: 'pdf' | 'xlsx' | 'csv';
  filters?: Record<string, unknown>;
  period_from?: string;
  period_to?: string;
  start_date?: string;
  end_date?: string;
  atendente_ids?: string[];
  professional_ids?: string[];
  especialidade?: string;
}): Promise<IExportCrcReportResponse> => {
  const { data } = await api.post('/crc/reports/export', payload);

  if (data && typeof data.content_base64 === 'string' && data.content_base64.length > 0) {
    const requestedFormat = (payload && payload.format) || 'xlsx';
    const rawText = base64ToString(data.content_base64);
    const sniffed = sniffContentFormat(rawText, data.mime_type);

    if (requestedFormat === 'pdf') {
      let pdfGenerated = false;
      try {
        const htmlToRender =
          sniffed === 'html'
            ? rawText
            : `<div><pre style="white-space:pre-wrap;font-family:Helvetica;font-size:10px;">${String(
                rawText || '',
              )
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')}</pre></div>`;
        const pdfBlob = await renderHtmlToPdfBlob({
          html: htmlToRender,
          size: 'A4',
          orientation: 'portrait',
        });
        if (pdfBlob && pdfBlob.size > 0) {
          const pdfFilename = ensureCorrectExtension(
            data.filename || `CRC_Relatorio_${Date.now()}`,
            'pdf',
            'pdf',
          );
          triggerBlobDownload(pdfBlob, pdfFilename);
          data.filename = pdfFilename;
          data.mime_type = 'application/pdf';
          data.format = 'pdf';
          pdfGenerated = true;
        }
      } catch (e) {
        console.warn('Falha ao renderizar PDF nativo, baixando conteúdo como fallback:', e);
      }
      if (!pdfGenerated) {
        try {
          const fallbackFilename = ensureCorrectExtension(
            data.filename || `CRC_Relatorio_${Date.now()}`,
            sniffed,
            requestedFormat,
          );
          const blob = base64ToBlob(
            data.content_base64,
            sniffed === 'html' ? 'text/html;charset=utf-8;' : (data.mime_type || 'application/octet-stream'),
          );
          triggerDownload(blob, fallbackFilename);
          data.filename = fallbackFilename;
          if (sniffed === 'html') {
            data.format = 'html';
            data.mime_type = 'text/html;charset=utf-8;';
          }
        } catch (inner) {
          if (typeof window !== 'undefined' && data.url) {
            window.open(data.url, '_blank');
          }
        }
      }
      return data;
    }

    try {
      const finalFilename = ensureCorrectExtension(
        data.filename || `CRC_Relatorio_${Date.now()}`,
        sniffed,
        requestedFormat,
      );
      const blob = base64ToBlob(data.content_base64, data.mime_type || 'application/octet-stream');
      triggerDownload(blob, finalFilename);
      data.filename = finalFilename;
      if (sniffed === 'csv' && data.format !== 'csv') {
        data.format = 'csv';
        data.mime_type = 'text/csv;charset=utf-8;';
      }
    } catch (e) {
      if (typeof window !== 'undefined') {
        console.warn('Não foi possível baixar automaticamente, tentando fallback:', e);
        if (data.url) {
          window.open(data.url, '_blank');
        }
      }
    }
  } else if (data && data.url) {
    try {
      window.open(data.url, '_blank');
    } catch (e) {
      console.warn('Não foi possível abrir a URL de download:', e);
    }
  }
  return data;
};

export const getCrcRecurrentProcedures = async (
  params: {
    start_date?: string;
    end_date?: string;
    period_from?: string;
    period_to?: string;
    atendente_ids?: string[];
    professional_ids?: string[];
    especialidade?: string;
  } = {},
): Promise<IRecurrentProceduresReport> => {
  const query = new URLSearchParams();
  if (params.start_date) query.set('start_date', params.start_date);
  if (params.end_date) query.set('end_date', params.end_date);
  if (params.period_from) query.set('period_from', params.period_from);
  if (params.period_to) query.set('period_to', params.period_to);
  if (params.atendente_ids && params.atendente_ids.length > 0) {
    params.atendente_ids.forEach((id) => query.append('atendente_ids[]', id));
  }
  if (params.professional_ids && params.professional_ids.length > 0) {
    params.professional_ids.forEach((id) => query.append('professional_ids[]', id));
  }
  if (params.especialidade) query.set('especialidade', params.especialidade);
  const qs = query.toString();
  const { data } = await api.get(
    `/crc/reports/recurrent-procedures${qs ? `?${qs}` : ''}`,
  );
  return data;
};

export const getCrcFutureContacts = async (
  params: {
    start_date?: string;
    end_date?: string;
    period_from?: string;
    period_to?: string;
    atendente_ids?: string[];
    professional_ids?: string[];
    especialidade?: string;
  } = {},
): Promise<IFutureContactsReport> => {
  const query = new URLSearchParams();
  if (params.start_date) query.set('start_date', params.start_date);
  if (params.end_date) query.set('end_date', params.end_date);
  if (params.period_from) query.set('period_from', params.period_from);
  if (params.period_to) query.set('period_to', params.period_to);
  if (params.atendente_ids && params.atendente_ids.length > 0) {
    params.atendente_ids.forEach((id) => query.append('atendente_ids[]', id));
  }
  if (params.professional_ids && params.professional_ids.length > 0) {
    params.professional_ids.forEach((id) => query.append('professional_ids[]', id));
  }
  if (params.especialidade) query.set('especialidade', params.especialidade);
  const qs = query.toString();
  const { data } = await api.get(
    `/crc/reports/future-contacts${qs ? `?${qs}` : ''}`,
  );
  return data;
};

export const getCrcReportsOverview = async (
  params: {
    start_date?: string;
    end_date?: string;
    period_from?: string;
    period_to?: string;
    atendente_ids?: string[];
    professional_ids?: string[];
    especialidade?: string;
  } = {},
): Promise<ICrcReportsOverview> => {
  const query = new URLSearchParams();
  if (params.start_date) query.set('start_date', params.start_date);
  if (params.end_date) query.set('end_date', params.end_date);
  if (params.period_from) query.set('period_from', params.period_from);
  if (params.period_to) query.set('period_to', params.period_to);
  if (params.atendente_ids && params.atendente_ids.length > 0) {
    params.atendente_ids.forEach((id) => query.append('atendente_ids[]', id));
  }
  if (params.professional_ids && params.professional_ids.length > 0) {
    params.professional_ids.forEach((id) => query.append('professional_ids[]', id));
  }
  if (params.especialidade) query.set('especialidade', params.especialidade);
  const qs = query.toString();
  const { data } = await api.get(`/crc/reports/overview${qs ? `?${qs}` : ''}`);
  return data;
};

const crc = {
  getCrcDashboardOverview,
  getCrcContacts,
  searchCrcContacts,
  postCrcContact,
  patchCrcContact,
  crcDeleteContact,
  getCrcPendencies,
  getCrcPendencyTabs,
  executePendencyQuickAction,
  resolveCrcPendency,
  generateCrcPendencies,
  crcSendWhatsapp,
  exportCrcReport,
  getCrcRecurrentProcedures,
  getCrcFutureContacts,
  getCrcReportsOverview,
};

export default crc;
